/*
 * Frido Voucher Redeem controller.
 *
 * Drives the "Redeem Voucher" flow opened from the My Account page: a cart-style
 * drawer with a single voucher-code field (no PIN) + a Redeem button. Submits the
 * code to the Gyftr → wallet port and maps every backend outcome to a visible
 * state. On success it shows a confirmation popup with the credited amount, then
 * sends the shopper to the wallet page (where balance + a "Deposited" transaction
 * already refresh on load).
 *
 * Endpoint (App Proxy, customer-authed):
 *   POST /apps/frido/v1/org/<org>/coin/gyftr/depositVoucher  body { voucherNumber }
 *   success → data { status, amountPaise, amountCoins }
 *   failure → throws via FridoOrders.proxy with { code, internalCode, message }
 *
 * Depends on window.FridoOrders (frido-orders-core.js) for proxy() + money/escape.
 *
 * DOM contract — the markup (button + drawer + popup) binds these data-attributes;
 * the visual skin lives in the section/snippet, this controller is presentation-
 * agnostic. Root element: [data-voucher-redeem].
 *   Config attrs on root:
 *     data-org           Gyftr org slug for the endpoint path (default "frido")
 *     data-wallet-path   where success navigates (default "/pages/wallet")
 *     data-login-path    where a 401 redirects (default "/customer_authentication/login")
 *   Open triggers (anywhere on the page): [data-voucher-open]
 *   Drawer:        [data-voucher-drawer]      — hidden attr off + .is-open to show
 *   Close/backdrop:[data-voucher-close]
 *   Input:         [data-voucher-input]
 *   Submit:        [data-voucher-submit]      — gets .is-loading + disabled in flight
 *   Inline error:  [data-voucher-error]       — hidden attr toggled; textContent set
 *   Success popup: [data-voucher-success]     — hidden attr toggled to reveal
 *     amount slot: [data-voucher-success-amount]
 *     continue:    [data-voucher-success-cta] — navigates to wallet (optional;
 *                  the controller also auto-navigates after SUCCESS_REDIRECT_MS)
 */
(function () {
  "use strict";

  // Frido gift vouchers are exactly 16 numeric digits — the field accepts digits
  // only, capped at 16. This is within the backend zod guard (10–24 alphanumerics
  // in depositVoucher.tsx), which remains the source of truth on submit.
  var VOUCHER_LEN = 16;
  var VOUCHER_RE = /^\d{16}$/;
  var SUCCESS_REDIRECT_MS = 1500;

  // Friendly copy per backend signal. Keyed first on internalCode (most specific),
  // then on the envelope code. Anything unmapped falls back to the backend message,
  // then a generic. Keep these aligned with depositVoucher.tsx + gyftr-port.server.ts.
  var INTERNAL_MESSAGES = {
    VALIDATION_ERROR: "Please enter a valid voucher code.",
    CUSTOMER_NO_PHONE: "Add a phone number to your account before redeeming a voucher.",
    CUSTOMER_PHONE_UNPARSEABLE: "We couldn't read the phone number on your account. Please update it and try again.",
    RAZORPAY_PROGRAM_NOT_CONFIGURED: "Voucher redemption is temporarily unavailable. Please try again later.",
    GYFTR_PORT_IN_FLIGHT: "This voucher is still being processed. Please wait a moment and check your wallet.",
    GYFTR_PORT_PERSIST_FAILED: "Something went wrong while crediting your wallet. Please try again.",
  };
  var CODE_MESSAGES = {
    RATE_LIMITED: "Too many attempts. Please wait a little while and try again.",
    PROVIDER_NOT_CONFIGURED: "Voucher redemption is temporarily unavailable. Please try again later.",
    NOT_LOGGED_IN: "Please log in to redeem a voucher.",
    BAD_RESPONSE: "We couldn't reach the server. Please try again.",
  };
  var GENERIC = "We couldn't redeem this voucher. Please check the code and try again.";

  function messageFor(err) {
    if (!err) return GENERIC;
    if (err.internalCode && INTERNAL_MESSAGES[err.internalCode]) return INTERNAL_MESSAGES[err.internalCode];
    if (err.code && CODE_MESSAGES[err.code]) return CODE_MESSAGES[err.code];
    // Backend voucher errors (invalid / expired / used / no balance) already
    // carry shopper-readable messages — prefer them over the generic.
    if (err.message && err.message !== "Request failed") return err.message;
    return GENERIC;
  }

  function init(root) {
    var F = window.FridoOrders;
    if (!F) return;

    var org = root.getAttribute("data-org") || "frido";
    var walletPath = root.getAttribute("data-wallet-path") || "/pages/wallet";
    var loginPath = root.getAttribute("data-login-path") || "/customer_authentication/login";
    // 'redirect' (standalone wallet page) navigates on success; 'inline'
    // (desktop account pane) refreshes the pane and dismisses without leaving.
    var successMode = root.getAttribute("data-success-mode") || "redirect";
    var endpoint = "/v1/org/" + encodeURIComponent(org) + "/coin/gyftr/depositVoucher";

    var drawer = root.querySelector("[data-voucher-drawer]");
    var input = root.querySelector("[data-voucher-input]");
    var submit = root.querySelector("[data-voucher-submit]");
    var errorEl = root.querySelector("[data-voucher-error]");
    var successEl = root.querySelector("[data-voucher-success]");
    var successAmount = root.querySelector("[data-voucher-success-amount]");
    var balanceEl = root.querySelector("[data-voucher-balance]");
    if (!drawer || !input || !submit) return;

    var submitting = false;

    // Redeem stays disabled until a full 16-digit code is entered (zero state).
    function syncSubmit() {
      if (!submitting) submit.disabled = (input.value || "").replace(/\D/g, "").length !== VOUCHER_LEN;
    }

    // ── State helpers ─────────────────────────────────────────────────────
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }
    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = "";
      errorEl.hidden = true;
    }
    function setLoading(on) {
      submitting = on;
      submit.classList.toggle("is-loading", on);
      input.disabled = on;
      if (on) submit.disabled = true;
      else syncSubmit();
    }

    // Non-critical chrome — fill the header balance when the drawer opens.
    function fetchBalance() {
      if (!balanceEl) return;
      F.proxy("/wallet/balance")
        .then(function (b) {
          if (b && b.balancePaise != null) {
            balanceEl.textContent = "Available Balance: " + F.rupeesFromPaise(b.balancePaise);
          }
        })
        .catch(function () {});
    }

    function goToWallet() {
      document.body.style.overflow = "";
      window.location.href = walletPath;
    }

    function closeSuccess() {
      if (successEl) successEl.hidden = true;
      document.body.style.overflow = "";
    }

    function showSuccess(data) {
      if (successAmount && data && data.amountPaise != null) {
        successAmount.textContent = F.rupeesFromPaise(data.amountPaise);
      }
      if (successEl) successEl.hidden = false;
      document.body.style.overflow = "hidden";
      if (successMode === "inline") {
        // Desktop account pane: refresh balance + transactions in place and let
        // the shopper dismiss the popup — never leave the shell. Auto-close as a
        // fallback if they don't tap the ✕.
        try { window.dispatchEvent(new CustomEvent("frido:wallet-refresh")); } catch (e) {}
        window.setTimeout(closeSuccess, SUCCESS_REDIRECT_MS + 1500);
      } else {
        // Standalone wallet page: auto-advance to the wallet so the credit shows.
        window.setTimeout(goToWallet, SUCCESS_REDIRECT_MS);
      }
    }

    // ── Drawer open/close (cart-drawer convention: hidden attr + .is-open) ──
    function openDrawer() {
      clearError();
      drawer.hidden = false;
      window.requestAnimationFrame(function () { drawer.classList.add("is-open"); });
      document.body.style.overflow = "hidden";
      fetchBalance();
      if (input && !input.disabled) {
        // Defer focus until the open transition has begun.
        window.setTimeout(function () { try { input.focus(); } catch (e) {} }, 50);
      }
    }
    function closeDrawer() {
      if (submitting) return; // don't yank the drawer out from under an in-flight redeem
      drawer.classList.remove("is-open");
      document.body.style.overflow = "";
      window.setTimeout(function () { drawer.hidden = true; }, 300);
    }

    // ── Submit ────────────────────────────────────────────────────────────
    function redeem() {
      if (submitting) return;
      var code = (input.value || "").replace(/\D/g, "");
      clearError();

      if (!code) {
        showError("Enter your voucher code.");
        input.focus();
        return;
      }
      if (!VOUCHER_RE.test(code)) {
        showError("Enter the full 16-digit voucher code.");
        input.focus();
        return;
      }

      setLoading(true);
      F.proxy(endpoint, { method: "POST", body: { voucherNumber: code } })
        .then(function (data) {
          // SUCCESS — wallet credited. Drop the form, reveal the confirmation.
          setLoading(false);
          closeDrawer();
          showSuccess(data);
        })
        .catch(function (err) {
          if (err && err.code === "NOT_LOGGED_IN") {
            var ret = encodeURIComponent("/pages/account");
            window.location.href = loginPath + "?return_to=" + ret;
            return;
          }
          setLoading(false);
          showError(messageFor(err));
          input.focus();
        });
    }

    // ── Wiring ────────────────────────────────────────────────────────────
    document.querySelectorAll("[data-voucher-open]").forEach(function (el) {
      el.addEventListener("click", function (e) {
        e.preventDefault();
        openDrawer();
      });
    });
    root.querySelectorAll("[data-voucher-close]").forEach(function (el) {
      el.addEventListener("click", closeDrawer);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      if (successEl && !successEl.hidden) {
        if (successMode === "inline") closeSuccess();
        else goToWallet();
        return;
      }
      if (!drawer.hidden) closeDrawer();
    });

    submit.addEventListener("click", function (e) {
      e.preventDefault();
      redeem();
    });
    input.addEventListener("keydown", function (e) {
      if (e.key === "Enter") { e.preventDefault(); redeem(); }
    });
    // Numeric-only, hard-capped at 16 digits; clear a stale error + toggle Redeem.
    input.addEventListener("input", function () {
      var cleaned = (input.value || "").replace(/\D/g, "").slice(0, VOUCHER_LEN);
      if (cleaned !== input.value) input.value = cleaned;
      clearError();
      syncSubmit();
    });
    syncSubmit();

    // Dismiss the success popup (✕ or overlay): inline mode just closes — the
    // pane already refreshed; redirect mode sends the shopper to the wallet.
    root.querySelectorAll("[data-voucher-success-close]").forEach(function (el) {
      el.addEventListener("click", function (e) {
        e.preventDefault();
        if (successMode === "inline") closeSuccess();
        else goToWallet();
      });
    });
  }

  function boot() { document.querySelectorAll("[data-voucher-redeem]").forEach(init); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
